// src/api/apiFactory.jsx
import axios from "axios";

const getBackendOrigin = () => {
  if (process.env.NODE_ENV === "production") {
    return window.location.origin;
  }

  // Development: Priority to matching browser's hostname to ensure SameSite cookies work
  const hostname = window.location.hostname;
  if (hostname === "127.0.0.1") {
    return "http://127.0.0.1:5000";
  }
  if (hostname === "localhost") {
    return "http://localhost:5000";
  }

  // Fallback if not standard local
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL.replace(/\/api\/v1\/?$/, "");
  }

  return "http://localhost:5000";
};

let csrfToken = null;
let csrfFetchPromise = null;

export async function initializeCSRFToken(forceRefresh = false) {
  // If we have a token and aren't forcing refresh, return it
  if (csrfToken && !forceRefresh) return csrfToken;

  // If a fetch is already in flight, wait for it
  if (csrfFetchPromise) return csrfFetchPromise;

  // Start new fetch
  csrfFetchPromise = (async () => {
    try {
      const url = `${getBackendOrigin()}/csrf-token/`;
      const response = await axios.get(url, {
        withCredentials: true,
        headers: { Accept: "application/json" },
        timeout: 8000, // Slightly longer timeout
      });

      const token = response.data?.csrf_token || response.data?.token || null;
      csrfToken = token;
      return token;
    } catch (error) {
      console.error("❌ CSRF fetch failed:", error.message);
      return null;
    } finally {
      // Clear promise so next call can retry if failed or force refresh
      csrfFetchPromise = null;
    }
  })();

  return csrfFetchPromise;
}


/**
 * Factory to create isolated axios instance
 * @param {string} tokenKey - localStorage key for access token (e.g. 'access' or 'admin_token')
 * @param {string} redirectBase - base path for login redirects (e.g. '/auth/login' or '/admin/auth/login')
 */


export function createAPI(tokenKey, redirectBase = "/auth/login") {
  const instance = axios.create({
    baseURL: `${getBackendOrigin()}/api/v1`,
    headers: { "Content-Type": "application/json" },
    withCredentials: true,
  });

  // === Request Interceptor ===
  instance.interceptors.request.use(
    async (config) => {
      try {
        const token = localStorage.getItem(tokenKey);
        if (token) config.headers.Authorization = `Bearer ${token}`;

        if (!csrfToken) await initializeCSRFToken();
        if (csrfToken) config.headers["X-CSRFToken"] = csrfToken;

        if (config.data instanceof FormData) delete config.headers["Content-Type"];
      } catch (err) {
        console.warn("⚠️ Skipped token or CSRF attach:", err);
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  // === Response Interceptor ===
  instance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const status = error?.response?.status;

      if (status === 400 && /csrf/i.test(error?.response?.data?.message || "")) {
        if (error.config._retry) {
          console.warn("⚠️ CSRF retry failed, stopping loop.");
          return Promise.reject(error);
        }
        console.warn("⚠️ CSRF expired — refreshing...");
        error.config._retry = true;

        const newToken = await initializeCSRFToken(true);
        if (newToken) {
          error.config.headers["X-CSRFToken"] = newToken;
        }

        return instance.request(error.config);
      }

      if (status === 401) {
        console.warn("⚠️ Unauthorized — redirecting to sign-in");
        localStorage.removeItem(tokenKey);
        localStorage.removeItem(tokenKey === "admin_token" ? "admin" : "user");

        // Set a flag so the login page can fire the notification toast
        // after the hard page reload (apiFactory lives outside React tree)
        try { sessionStorage.setItem("__session_expired", "1"); } catch (_) { }

        try {
          const attemptedPath = `${window.location.pathname || ""}${window.location.search || ""}`;
          const encoded = encodeURIComponent(attemptedPath);
          window.location.href = `${redirectBase}?next=${encoded}&reason=session_expired`;
        } catch (e) {
          window.location.href = `${redirectBase}?reason=session_expired`;
        }
      }

      return Promise.reject(error);
    }
  );

  return instance;
}
